'use client';

import { documentToReactComponents } from '@contentful/rich-text-react-renderer';
import { HTMLAttributes, useContext, useEffect, useId, useRef, useState } from 'react';

import { isRichTextDocument } from '@/lib/contentful/utils';
import { cn } from '@/lib/utils';
import { ChevronDownIcon } from 'lucide-react';
import { AccordionContext } from './accordionExperience';
import styles from './accordionExperience.module.scss';

type AccordionExperienceItemProps = {
  index: number;
  title: string;
  // eslint-disable-next-line
  content: any;
  size: string;
  colorHeading: string;
  colorText: string;
  colorLink: string;
  textAlign: string;
};

const slugify = (text: string) =>
  text
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');

export const AccordionItemExperience = ({
  // index,
  title,
  content,
  size,
  colorHeading,
  colorText,
  colorLink,
  textAlign,
  children,
  className,
  ...props
}: AccordionExperienceItemProps & HTMLAttributes<HTMLDivElement>) => {
  const accordionContext = useContext(AccordionContext);
  const hash = useId();
  const contentRef = useRef<HTMLDivElement>(null);
  const [contentHeight, setContentHeight] = useState(0);

  useEffect(() => {
    if (contentRef.current) {
      const resizeObserver = new ResizeObserver(() => {
        if (contentRef.current) {
          setContentHeight(contentRef.current.scrollHeight);
        }
      });

      resizeObserver.observe(contentRef.current);
      setContentHeight(contentRef.current.scrollHeight);

      return () => resizeObserver.disconnect();
    }
  }, [children]);

  if (!accordionContext) {
    return (
      <div className={cn(styles.warning)} {...props}>
        <h5>Remove this instance!</h5>
        <p>
          This component must be placed inside an <strong>Accordion Container</strong>.
        </p>
      </div>
    );
  }

  const { openItems, toggleItem, instance } = accordionContext;

  if (!title) {
    return (
      <div
        className={cn(
          styles.component_item,
          className,
          size,
          colorHeading,
          colorText,
          colorLink,
          textAlign,
        )}
        {...props}
      >
        <span className={styles.noquest}>Please add the</span>
      </div>
    );
  }
  const id = `${slugify(title)}${instance}${hash}`;
  const isOpen = openItems.includes(id);

  return (
    <div
      className={cn(
        styles.component_item,
        className,
        size,
        colorHeading,
        colorText,
        colorLink,
        textAlign,
      )}
      {...props}
    >
      <button
        id={`accordion-trigger-${id}`}
        onClick={() => toggleItem(id)}
        aria-expanded={isOpen}
        aria-controls={`accordion-content-${id}`}
        className={styles.component_trigger}
      >
        <span className={styles.component_title}>{title}</span>
        <div className={styles.component_icon_container}>
          <span
            className={cn(styles.component_icon_wrapper, {
              [styles.component_icon_wrapper_open]: isOpen,
            })}
            aria-hidden="true"
          >
            <ChevronDownIcon className={styles.component_icon} />
          </span>
        </div>
      </button>
      <div
        id={`accordion-content-${id}`}
        className={cn(styles.component_content, {
          [styles.component_content_open]: isOpen,
        })}
        role="region"
        aria-labelledby={`accordion-trigger-${id}`}
        style={{
          maxHeight: isOpen ? `${contentHeight}px` : '0px',
        }}
      >
        <div
          ref={contentRef}
          className={cn(styles.component_content_inner, {
            [styles.component_content_inner_open]: isOpen,
          })}
        >
          {isRichTextDocument(content)
            ? documentToReactComponents(content)
            : content
              ? content
              : ''}
        </div>
      </div>
    </div>
  );
};
